import { useMemo } from "react";

import { useCollectionData, useLoadingValues } from "astrid-firestore/src/hooks";
import { useProfile } from "astrid-web/src/features/authentication/state/profile";
import useHasRight from "astrid-web/src/features/authorization/hooks/useHasRight";

import useTodoHeaderTranslations from "./useTodoHeaderTranslations";
import useTodosQuery from "./useTodosQuery";

function filterProductionLanguage(language) {
	return (todo) => !language || todo.languages?.includes(language);
}

function filterType(type) {
	return (todo) => !type || todo.type === type;
}

function filterAssigned(userId) {
	return (todo) => !userId || (userId === "notAssigned" ? todo.userIds.length === 0 : todo.userIds.includes(userId));
}

export default function useTodos(
	{
		type = null,
		order = "desc",
		productionLanguage = null,
		showResolved = false,
		userId = null,
		organization = null,
	},
	initialTodos = [],
) {
	const user = useProfile();
	const hasRight = useHasRight();

	const enabledTodos = useTodoHeaderTranslations();

	const [[unresolvedTodos = initialTodos, resolvedTodos = initialTodos], loading, error] = useLoadingValues(
		useCollectionData(
			useTodosQuery({
				userId: user.id,
				organizationId: hasRight("todos.view") && organization?.id,
			}),
		),
		useCollectionData(
			useTodosQuery({
				userId: user.id,
				organizationId: hasRight("todos.view") && organization?.id,
				resolved: true,
			}),
		),
	);

	const todos = useMemo(() => {
		return [...unresolvedTodos, ...resolvedTodos].sort((a, b) =>
			a.createdAt.toDate() < b.createdAt.toDate() ? 1 : -1,
		);
	}, [unresolvedTodos, resolvedTodos]);

	const sortedTodos = useMemo(() => {
		return (order === "desc" ? todos : todos.slice().reverse())
			.filter((todo) => enabledTodos[todo.type])
			.filter((todo) => showResolved || !todo.resolved?.status)
			.filter(filterProductionLanguage(productionLanguage));
	}, [enabledTodos, order, productionLanguage, showResolved, todos]);

	const filteredTodos = useMemo(() => {
		return sortedTodos
			.filter(filterType(type))
			.filter(filterAssigned(userId))
			.filter(filterProductionLanguage(productionLanguage));
	}, [productionLanguage, sortedTodos, type, userId]);

	return { sortedTodos, filteredTodos, loading, error };
}
